import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import MetaTags from 'react-meta-tags';
import './followers.css';

import BlueTick from '../../images/blue-tick.png';
import CrossPink from '../../images/cross.png';
import FollowerRow from '../../common/FollowerRow';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import useDidMountEffect from '../../utils/useDidMountEffect';
import currency from 'currency.js';
import { asyncDebouncer, numberWithCommas } from '../../utils/helper';
import { captureMessage } from '@sentry/react';
import UnfollowReasonModal from './UnfollowReasonModal';

// MUI Table
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { CircularProgress, useMediaQuery } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { ROLES } from '../../utils/constants';
import DefaultPUWidget from '../../Widgets/PU/Default';
import DefaultWidget from '../../Widgets/SU/Default';
import { connect } from 'react-redux';
import CloseButton from '../../common/CloseButton';
import { useHistory } from 'react-router';
import PageHeader from '../../common/PageHeader';
/**
 * @typedef {Object} Order
 * @prop {number} ASC - Ascending
 * @prop {number} DESC - Descending
 *
 */

/**
 * @type {Order}
 */
const Order = {
    ASC: 0,
    DESC: 1,
};

/**
 *
 * @param {{
 *  name: 'up' | 'down'
 * }} params0
 * @returns
 */
const CaretIcon = ({ name }) => {
    const Icons = {
        down: () => (
            <svg xmlns="http://www.w3.org/2000/svg" height="1em" width="1em" viewBox='0 0 48 48'>
                <path d="m24 30-10-9.95h20Z" fill='currentColor' />
            </svg>
        ),
        up: () => (
            <svg xmlns="http://www.w3.org/2000/svg" height="1em" width="1em" viewBox='0 0 48 48'>
                <path d="m14 28 10-10.05L34 28Z" fill='currentColor' />
            </svg>
        )
    };
    const SelectedIcon = Icons[name];

    return <SelectedIcon />;
};

const getDefaultOrderFilters = (sortBy) => {
    return {
        highestPaying: {
            sortBy: 1,
            order: Order.ASC,
            active: sortBy === 1,
        },
        highestTips: {
            sortBy: 2,
            order: Order.ASC,
            active: sortBy === 2,
        },
        likes: {
            sortBy: 3,
            order: Order.ASC,
            active: sortBy === 3,
        },
        muted: {
            sortBy: 4,
            order: Order.ASC,
            active: sortBy === 4,
        },
        unsubscribed: {
            sortBy: 5,
            order: Order.ASC,
            active: sortBy === 5,
        },
        userName: {
            sortBy: 6,
            order: Order.ASC,
            active: sortBy === 6,
        },
        unfollow_reason: {
            sortBy: 7,
            order: Order.ASC,
            active: sortBy === 7,
        },
    };
};

// MUI styled table components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        // backgroundColor: theme.palette.common.black,
        backgroundColor: '#FBFBFB',
        color: '#5f5f5f',
        width: '100%',
        padding: '8px',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: '12px',
    },
}));

const StyledTableHeaderRow = styled(TableRow)(({ theme }) => ({
    // set first child's border-radius to 4px
    '&:first-of-type': {
        borderRadius: '4px',
    },
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    borderRadius: '4px',
}));

const Followers = ({
    user
}) => {
    const history = useHistory();
    const isMobile = useMediaQuery('(max-width: 899px)');

    const [followers, setFollowers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [isGettingCount, setIsGettingCount] = useState(false);
    const [subscribedTotal, setSubscribedTotal] = useState(0);
    const [muteCount, setMuteCount] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [hasMore, setHasMore] = useState(false);
    const [unsubscribedCount, setUnsubscribedCount] = useState(0);
    const [blockUpdate, setBlockUpdate] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [tipsPpvs, setTipsPpvs] = useState(0);
    const [paid, setPaid] = useState(0);
    const [name, setName] = useState('');
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState(6);
    const [shouldFetch, setShouldFetch] = useState(false);
    const [gettingTotals, setgettingTotals] = useState(false);
    const [expiredCount, setExpiredCount] = useState(0);
    const [orderFilter, setOrderFilter] = useState(getDefaultOrderFilters(sortBy));
    const [showUnfollowReasonModal, setShowUnfollowReasonModal] = useState(false);
    const [freeCount, setFreeCount] = useState(0);
    const [total, setTotal] = useState(0);

    const [order, setOrder] = useState(orderFilter.userName.order);
    const unsubCallFromRef = useRef(null);

    const observer = useRef();
    const lastFollowerElementRef = useCallback(node => {
        if (busy) return;
        if (observer.current) {
            observer.current.disconnect();
        }
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(page => page + 1);
            }
        });

        if (node) observer.current.observe(node);

        return () => {
            observer.current.disconnect();
        };
    }, [busy, hasMore]);

    useEffect(() => {
        if (blockUpdate) {
            getTotals();
        }
    }, [blockUpdate]);

    const getCounts = () => {
        setIsGettingCount(true);
        axios.get('/api/followers', {
            params: {
                count: 'unsubscribed,subscribed,expired,free,total'
            }
        })
            .then(response => {
                setUnsubscribedCount(response.data.unsubscribed);
                setSubscribedTotal(response.data.subscribed);
                setExpiredCount(response.data.expired);
                setFreeCount(response.data.free);
                setTotal(response.data.total);
                setBlockUpdate('');
            })
            .catch(error => {
                captureMessage(error);
            })
            .finally(() => {
                setIsGettingCount(false);
            });
    };

    const getFollowers = () => {
        setBusy(true);
        axios.get('/api/followers', {
            params: {
                per_page: perPage,
                page: page,
                ...filters,
                sortBy: sortBy,
                order: Boolean(order) ? 'DESC' : 'ASC'
            }
        })
            .then(response => {
                setHasMore(response.data.data.length > 0);
                const followersArr = new Set(followers.map(follower => follower.id));
                const merged = [
                    ...followers,
                    ...response.data.data.filter(d => !followersArr.has(d.id))
                ];
                setFollowers(merged);
                setShouldFetch(false);
            })
            .catch(error => {
                console.log({ error });
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const getTotals = () => {
        setgettingTotals(true);
        axios.get('/api/followers/totals')
            .then(response => {
                setLikeCount(response.data.likes);
                setMuteCount(response.data.muted);
                setPaid(response.data.earnings);
                setTipsPpvs(response.data.ppv_tips);
                setgettingTotals(false);
            })
            .catch(error => {
                captureMessage(error);
                setgettingTotals(false);
            });
    };

    useEffect(() => {
        getCounts();
        getTotals();
    }, []);

    useDidMountEffect(() => {
        getFollowers();
    }, [page]);

    useEffect(() => {
        if (blockUpdate) {
            getCounts();
            const followerIndex = followers.findIndex(follower => blockUpdate == follower.id);
            followers[followerIndex].mute_id = !!!followers[followerIndex].mute_id;
            setFollowers(followers);
        }
    }, [blockUpdate]);



    // useEffect(() => {
    //     // const likes = followers.reduce((sum, follower) => {
    //     //   return sum += parseInt(follower.likes_count)
    //     // }, 0)
    //     // setLikeCount(likes)

    //     // const muted = followers.reduce((sum, follower) => {
    //     //   return sum += follower.blocked_at ? 1 : 0
    //     // }, 0)
    //     // setMuteCount(muted)

    //     // const totalPaid = followers.reduce((sum, follower) => {
    //     //   return sum += follower.total_earnings ? parseFloat(follower.total_earnings) : 0
    //     // }, 0)
    //     // setPaid(totalPaid)

    //     // const totalTipsPpvs = followers.reduce((sum, follower) => {
    //     //   return sum += parseFloat(follower.tip_ppv_earnings)
    //     // }, 0)
    //     // setTipsPpvs(totalTipsPpvs)
    // }, [followers, blockUpdate]);

    const handleSearch = asyncDebouncer(async () => {
        // e.preventDefault();
        setFollowers([]);
        setFilters({
            ...filters,
            name: name
        });
        if (page > 1) {
            setPage(1);
        } else {
            setShouldFetch(true);
        }
    }, 800);

    const handleSortBy = e => {
        e.preventDefault();
        let sort = Number(e.target.value);
        let order = 0;
        const colKey = Object.keys(orderFilter).find(key => orderFilter[key].sortBy === sort);

        if (sort === 5 || sort === 6) {
            order = 0;
        } else {
            order = 1;
        }

        setSortBy(sort);
        setOrder(order);

        setOrderFilter((state) => {
            const resetActiveState = {};
            Object.keys(state).forEach((key) => {
                resetActiveState[key] = state[key];
                resetActiveState[key].active = false;
                resetActiveState[key].order = Order.ASC;
            });
            return {
                ...resetActiveState,
                [colKey]: {
                    ...state[colKey],
                    order,
                    active: true,
                    ...(
                        colKey === 'unsubscribed' &&
                        unsubCallFromRef.current !== 'onOrderChange' &&
                        { order: Order.ASC }
                    )
                }
            };
        });

        setFollowers([]);
        if (page > 1) {
            setPage(1);
        } else {
            setShouldFetch(true);
        }
        if (Number(e.target.value) === 5) unsubCallFromRef.current = 'handleSortBy';
    };

    useDidMountEffect(() => {
        if (shouldFetch) {
            getFollowers();
        }
    }, [shouldFetch]);

    useDidMountEffect(() => {
        handleSearch();
    }, [name]);


    const onOrderChange = (e) => {
        const colKey = e.target.getAttribute('data-column');
        setOrderFilter((state) => {
            const currentColumn = state[colKey];
            const _order = sortBy === currentColumn.sortBy ? Number(!Boolean(state[colKey].order)) : !state[colKey].order;
            const resetActiveState = {};
            Object.keys(state).forEach((key) => {
                resetActiveState[key] = state[key];
                resetActiveState[key].active = false;
                if (key !== colKey) {
                    resetActiveState[key].order = Order.ASC;
                }
            });
            return {
                ...resetActiveState,
                [colKey]: {
                    ...state[colKey],
                    order: _order,
                    active: true,
                    ...(
                        colKey === 'unsubscribed' &&
                        unsubCallFromRef.current !== 'onOrderChange' &&
                        { order: Order.DESC }
                    )
                }
            };
        });
        if (colKey === 'unsubscribed')
            unsubCallFromRef.current = 'onOrderChange';
        else
            unsubCallFromRef.current = 'onOrderChange_1';
    };

    useEffect(() => {
        // if(callFromRef.current === 'onOrderChange'){
        const activeColumn = orderFilter[Object.keys(orderFilter).find((key) => orderFilter[key].active === true)];
        setSortBy(activeColumn.sortBy);
        setOrder(activeColumn.order ? 1 : 0);
        setFollowers([]);
        if (page > 1) {
            setPage(1);
        } else {
            setShouldFetch(true);
        }
        // }
    }, [orderFilter]);

    const handleShowUnfollowReasonModal = () => {
        setShowUnfollowReasonModal(state => !state);
    };

    const clearSearchText = () => {
        setName('');
    };

    return <div className="container followerspage-container pt-4 mb-4">
        <MetaTags>
            <title>Followers &#8211; Best way to make money as an influencer for your exclusive content</title>
            <meta name="description" content="Privaposts is a social media platform that lets influencers make money from their followers by keeping exclusive content hidden until a monthly subscription price has been paid." />
        </MetaTags>

        <Grid2
            container
            spacing={!isMobile ? 2 : 0}
        >
            <Grid2 md={8}>
                <div className='follower-filter-con'>
                    <PageHeader title="Followers" />
                    <div
                        className="follower-form-container mb-2 mt-4"
                    >
                        <div className="si-con">
                            <span className="search-wrapper">
                                <input
                                    type="text"
                                    className="search-key"
                                    placeholder="Search User"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                {
                                    name && <i className="fal fa-times search-clear-icon" onClick={clearSearchText} />
                                }
                            </span>
                        </div>
                        <div className="fsf-con">
                            <select className="follower-search-filter" name="filters" onChange={handleSortBy} value={sortBy}>
                                {/* <option disabled>Sorting Options</option> */}
                                <option value="6">User Name</option>
                                <option value="1">Highest Paying Followers</option>
                                <option value="2">Highest Total Tips &amp; PPVs</option>
                                <option value="3">Most Likes</option>
                                <option value="4">Muted Users</option>
                                <option value="5">Unsubscribed Followers</option>
                            </select>
                        </div>
                        <div className="urs-btn-con">
                            <Button
                                className=""
                                style={{
                                    textTransform: 'capitalize'
                                }}
                                onClick={handleShowUnfollowReasonModal}
                            >
                                Unfollow Reasons Summary
                            </Button>
                        </div>
                    </div>
                </div>


                <div style={{ fontSize: '12px', padding: '12px' }} className="my-1">
                    The "Followers" total below is (Subscribed + Unsubscribed) users.
                </div>

                <div className="followers-legend-container" style={{ lineHeight: '14px', padding: '12px' }}>
                    <p><b>Subscription Status of Followers (Sub Status):</b></p>
                    <table border="0" className="legend_tbl">
                        <tbody>
                            <tr>
                                <td className="legend_center">
                                    <LazyLoadImage
                                        src={BlueTick}
                                        alt="Blue Tick"
                                        width={14}
                                        style={{ width: '14px' }}
                                    />
                                </td>
                                <td>=</td>
                                <td style={{ fontSize: '12px' }}>
                                    <span className="subscribed_color">SUBSCRIBED</span>
                                    : Follower will be renewing their subscription to you.
                                </td>
                            </tr>
                            <tr>
                                <td className="legend_center"><b>D MMM YYYY</b></td>
                                <td>=</td>
                                <td style={{ fontSize: '12px' }}>
                                    <b>UNSUBSCRIBED</b>
                                    : Followers have cancelled their renewal and will have no access to your profile after this date.
                                </td>
                            </tr>
                            <tr>
                                <td className="legend_center">
                                    <LazyLoadImage
                                        src={CrossPink}
                                        alt="Cross Pink"
                                        width={14}
                                        style={{ width: '14px' }} />
                                </td>
                                <td>=</td>
                                <td style={{ fontSize: '12px' }}>
                                    <span className="expired_color">EXPIRED</span>
                                    : Subscription ended and user has no access to your profile.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="followers-data-container">
                    <div className="followers-data">
                        <div className="followers-body-content">
                            <div className="followers-total-table-content">
                                <TableContainer component={Paper}>
                                    <Table>
                                        <StyledTableHead>
                                            <StyledTableHeaderRow>
                                                <StyledTableCell colSpan={7}>
                                                    Followers({isGettingCount ? <Spinner animation="border" size="sm" /> : numberWithCommas(total)})
                                                </StyledTableCell>
                                            </StyledTableHeaderRow>

                                            <StyledTableHeaderRow>
                                                <StyledTableCell colSpan={7}>
                                                    Subscribed({isGettingCount ? <Spinner animation="border" size="sm" /> : numberWithCommas(subscribedTotal)})    Unsubscribed({isGettingCount ? <Spinner animation="border" size="sm" /> : numberWithCommas(unsubscribedCount)})  Expired({isGettingCount ? <Spinner animation="border" size="sm" /> : numberWithCommas(expiredCount)}) Free({isGettingCount ? <Spinner animation="border" size="sm" /> : numberWithCommas(freeCount)})
                                                </StyledTableCell>
                                            </StyledTableHeaderRow>

                                            <StyledTableHeaderRow>
                                                <StyledTableCell>
                                                    Total
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {currency(paid).format()}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {currency(tipsPpvs).format()}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {numberWithCommas(likeCount)}
                                                </StyledTableCell>
                                                <StyledTableCell colSpan={2}>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {numberWithCommas(muteCount)}
                                                </StyledTableCell>
                                            </StyledTableHeaderRow>

                                            <StyledTableHeaderRow>
                                                <StyledTableCell align="center" data-column="userName" onClick={onOrderChange}>
                                                    <div className='th-lbl'>
                                                        <span>User Name</span>
                                                        <span className='th-ico'>
                                                            <CaretIcon name={orderFilter['userName'].order === Order.ASC ? 'up' : 'down'} />
                                                        </span>

                                                    </div>
                                                </StyledTableCell>

                                                <StyledTableCell align="center" data-column="highestPaying" onClick={onOrderChange}>
                                                    <div className='th-lbl'>
                                                        <span>Total Paid</span>
                                                        <span className='th-ico'>
                                                            <CaretIcon name={orderFilter['highestPaying'].order === Order.ASC ? 'up' : 'down'} />
                                                        </span>
                                                    </div>
                                                </StyledTableCell>

                                                <StyledTableCell align="center" data-column="highestTips" onClick={onOrderChange}>
                                                    <div className='th-lbl'>
                                                        <span>Tips + PPVs</span>
                                                        <span className='th-ico'>
                                                            <CaretIcon name={orderFilter['highestTips'].order === Order.ASC ? 'up' : 'down'} />
                                                        </span>
                                                    </div>
                                                </StyledTableCell>

                                                <StyledTableCell align="center" data-column="unsubscribed" onClick={onOrderChange}>
                                                    <div className='th-lbl'>
                                                        <span>Sub Status</span>
                                                        <span className='th-ico'>
                                                            <CaretIcon name={orderFilter['unsubscribed'].order === Order.ASC ? 'up' : 'down'} />
                                                        </span>
                                                    </div>
                                                </StyledTableCell>

                                                <StyledTableCell align="center" data-column="likes" onClick={onOrderChange}>
                                                    <div className='th-lbl'>
                                                        <span>Likes</span>
                                                        <span className='th-ico'>
                                                            <CaretIcon name={orderFilter['likes'].order === Order.ASC ? 'up' : 'down'} />
                                                        </span>
                                                    </div>
                                                </StyledTableCell>

                                                <StyledTableCell align="center" data-column="unfollow_reason" onClick={onOrderChange}>
                                                    <div className='th-lbl'>
                                                        <span>Unfollow Reason</span>
                                                        <span className='th-ico'>
                                                            <CaretIcon name={orderFilter['unfollow_reason'].order === Order.ASC ? 'up' : 'down'} />
                                                        </span>
                                                    </div>
                                                </StyledTableCell>

                                                <StyledTableCell align="center" data-column="muted" onClick={onOrderChange}>
                                                    <div className='th-lbl'>
                                                        <span>Mute</span>
                                                        <span className='th-ico'>
                                                            <CaretIcon name={orderFilter['muted'].order === Order.ASC ? 'up' : 'down'} />
                                                        </span>
                                                    </div>
                                                </StyledTableCell>
                                            </StyledTableHeaderRow>
                                        </StyledTableHead>
                                        <TableBody>
                                            {
                                                followers.length > 0
                                                    ? followers.map((follower, index) => {
                                                        if (followers.length === index + 1) {
                                                            return <FollowerRow
                                                                ref={lastFollowerElementRef}
                                                                row={follower}
                                                                key={follower.id}
                                                                setBlockUpdate={setBlockUpdate} />;
                                                        } else {
                                                            return <FollowerRow
                                                                row={follower}
                                                                key={follower.id}
                                                                setBlockUpdate={setBlockUpdate} />;
                                                        }
                                                    })
                                                    : null
                                            }

                                            {
                                                !busy && followers.length <= 0
                                                    ? <tr>
                                                        <td colSpan="7">
                                                            <span>No followers search results.</span>
                                                        </td>
                                                    </tr>
                                                    : null
                                            }

                                            {
                                                busy
                                                    ? <tr>
                                                        <td colSpan="7" align='center'>
                                                            <CircularProgress size={20} />
                                                        </td>
                                                    </tr>
                                                    : null
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </div>
                    </div>
                </div>
                <UnfollowReasonModal
                    show={showUnfollowReasonModal}
                    setShow={setShowUnfollowReasonModal}
                />
            </Grid2>
            {
                !isMobile
                    ? <Grid2 className='pu-homepage__right' md={4}>
                        {
                            user && user.role == ROLES.PU ? <DefaultPUWidget /> : null
                        }
                        {
                            user && user.role == ROLES.SU ? <DefaultWidget /> : null
                        }
                    </Grid2>
                    : null
            }
        </Grid2>

    </div>;
};

const mapStateToProps = state => {
    return {
        user: state.user.user,
    };
};

export default connect(mapStateToProps)(Followers);
